import { useState, useEffect, useRef } from "react";
import Topbar from "../components/layout/Topbar.jsx";
import { syncAll, syncCompanies, syncAssets, syncTickets } from "../services/syncService.js";
import api from "../config/api";

// ─────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────

function timeAgo(iso) {
  if (!iso) return "jamais";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)    return `il y a ${diff} sec`;
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
}

function timeIn(seconds) {
  if (seconds <= 0) return "maintenant";
  if (seconds < 60) return `dans ${seconds} sec`;
  return `dans ${Math.floor(seconds / 60)} min ${seconds % 60} sec`;
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr-FR");
}

/** Exporte les logs de session en CSV */
function exportLogs(logs) {
  const header = "Type,Statut,Créés,Mis à jour,Ignorés,Durée,Date/heure,Erreur";
  const rows = logs.map(l => {
    const d = l.data;
    const created = d ? (d.companies?.created ?? 0) + (d.assets?.created ?? 0) + (d.tickets?.created ?? 0) : 0;
    const updated = d ? (d.companies?.updated ?? 0) + (d.assets?.updated ?? 0) + (d.tickets?.updated ?? 0) : 0;
    const skipped = d ? (d.companies?.skipped ?? 0) + (d.assets?.skipped ?? 0) + (d.tickets?.skipped ?? 0) : 0;
    return [
      l.type === "all" ? "Complète" : l.type,
      l.status,
      created,
      updated,
      skipped,
      l.duration,
      fmt(l.at),
      l.error || "",
    ].join(",");
  });
  const csv  = [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `sync-logs-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────
//  COMPOSANT — BARRE DE PROGRESSION ANIMÉE
//  Simule visuellement l'avancement d'une sync
//  (le backend ne streame pas de progression).
// ─────────────────────────────────────────────

function ProgressBar({ active, color = "var(--accent)" }) {
  const [pct, setPct] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (active) {
      setPct(0);
      // Simule une progression rapide jusqu'à 85% puis ralentit
      // La barre atteindra 100% quand active passera à false
      let current = 0;
      ref.current = setInterval(() => {
        current += current < 70 ? 3 : current < 85 ? 0.5 : 0.1;
        setPct(Math.min(current, 90));
      }, 100);
    } else {
      if (ref.current) clearInterval(ref.current);
      setPct(prev => prev > 0 ? 100 : 0);
      setTimeout(() => setPct(0), 600);
    }
    return () => { if (ref.current) clearInterval(ref.current); };
  }, [active]);

  if (pct === 0) return null;

  return (
    <div style={{ height:3, background:"rgba(255,255,255,.08)", borderRadius:99, overflow:"hidden", margin:"8px 0 0" }}>
      <div style={{
        height:"100%", borderRadius:99, background:color,
        width:`${pct}%`,
        transition: pct === 100 ? "width .3s ease" : "width .1s linear",
        boxShadow: `0 0 8px ${color}88`,
      }}/>
    </div>
  );
}

// ─────────────────────────────────────────────
//  COMPOSANT — CARTE SYNC INDIVIDUELLE
// ─────────────────────────────────────────────

function SyncCard({ typeKey, label, icon, color, status, onSync, disabled, dbCount }) {
  const s = status;

  return (
    <div className="itm-card" style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {/* En-tête */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:38, height:38, borderRadius:10, background:color+"22", color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, flexShrink:0 }}>
            <i className={`bi ${icon}`}/>
          </div>
          <div>
            <div style={{ fontWeight:600, fontSize:14, color:"var(--text-1)" }}>{label}</div>
            {dbCount !== undefined && (
              <div style={{ fontSize:11, color:"var(--text-3)" }}>
                {dbCount} en base
              </div>
            )}
          </div>
        </div>
        <button
          className="btn-itm btn-itm-outline"
          style={{ padding:"5px 12px", fontSize:12 }}
          onClick={onSync}
          disabled={disabled}
        >
          <i className={`bi bi-arrow-repeat ${s?.syncing ? "spin" : ""} me-1`}/>
          {s?.syncing ? "En cours…" : "Sync"}
        </button>
      </div>

      {/* Barre de progression */}
      <ProgressBar active={!!s?.syncing} color={color}/>

      {/* Résultat */}
      {s?.result && !s.syncing && (
        <div style={{ fontSize:12, background:"rgba(16,185,129,.08)", border:"1px solid rgba(16,185,129,.2)", borderRadius:8, padding:"8px 10px" }}>
          <div style={{ display:"flex", gap:14 }}>
            <span style={{ color:"var(--success)" }}>
              <i className="bi bi-plus-circle me-1"/>+{s.result.created ?? 0} créés
            </span>
            <span style={{ color:"var(--accent)" }}>
              <i className="bi bi-arrow-repeat me-1"/>{s.result.updated ?? 0} mis à jour
            </span>
            {(s.result.skipped ?? 0) > 0 && (
              <span style={{ color:"var(--text-3)" }}>
                <i className="bi bi-dash-circle me-1"/>{s.result.skipped} ignorés
              </span>
            )}
          </div>
        </div>
      )}

      {/* Erreur */}
      {s?.error && !s.syncing && (
        <div style={{ fontSize:12, color:"var(--danger)", background:"var(--danger-dim)", border:"1px solid rgba(239,68,68,.25)", borderRadius:8, padding:"8px 10px" }}>
          <i className="bi bi-exclamation-triangle me-1"/>{s.error}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  PAGE PRINCIPALE
// ─────────────────────────────────────────────

export default function SyncPage() {

  // ── Statut de chaque sync ─────────────────────
  const [syncStatus, setSyncStatus] = useState({
    companies: null,
    assets:    null,
    tickets:   null,
    all:       null,
  });

  // ── Stats actuelles en base ───────────────────
  const [dbStats, setDbStats] = useState(null);

  // ── Compteur prochaine sync ───────────────────
  const [countdown, setCountdown] = useState(null);
  const countdownRef = useRef(null);

  // ── Journal de session ────────────────────────
  const [logs,    setLogs]    = useState([]);
  const [confirm, setConfirm] = useState(false); // confirmation sync complète

  // Charge les stats en base au montage
  useEffect(() => {
    loadDbStats();
  }, []);

  const loadDbStats = async () => {
    try {
      // On appelle le dashboard admin pour récupérer les compteurs
      const res = await api.get("/dashboard/admin");
      const d   = res.data?.data ?? res.data;
      setDbStats({
        companies: null, // pas dans le dashboard, on utilise le résultat de sync
        assets:    d?.metrics?.totalAssets   ?? null,
        tickets:   null, // idem
      });
    } catch {
      // silencieux — les stats ne sont pas critiques
    }
  };

  const startCountdown = (seconds) => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setCountdown(seconds);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(countdownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  // ── Lance une sync ────────────────────────────
  const runSync = async (type) => {
    const services = {
      all:       syncAll,
      companies: syncCompanies,
      assets:    syncAssets,
      tickets:   syncTickets,
    };

    setSyncStatus(prev => ({ ...prev, [type]: { syncing: true, result: null, error: null } }));
    const startedAt = new Date();

    try {
      const res      = await services[type]();
      const data     = res?.data ?? res;
      const duration = ((Date.now() - startedAt.getTime()) / 1000).toFixed(1) + "s";

      // Pour une sync individuelle, le data EST le result directement
      const result = type === "all" ? data : data;

      setLogs(prev => [{ id: Date.now(), type, status:"SUCCESS", data: result, duration, at: startedAt }, ...prev].slice(0, 50));
      setSyncStatus(prev => ({ ...prev, [type]: { syncing: false, result: type === "all" ? result : result, error: null } }));

      if (type === "all") startCountdown(300);

      // Rafraîchit les stats en base après sync
      loadDbStats();

    } catch (err) {
      const duration = ((Date.now() - startedAt.getTime()) / 1000).toFixed(1) + "s";
      const errMsg   = err.message || "Erreur inconnue";
      setLogs(prev => [{ id: Date.now(), type, status:"FAILED", error: errMsg, duration, at: startedAt }, ...prev].slice(0, 50));
      setSyncStatus(prev => ({ ...prev, [type]: { syncing: false, result: null, error: errMsg } }));
    }
  };

  const isAnySyncing = Object.values(syncStatus).some(s => s?.syncing);
  const lastAll      = syncStatus.all?.result;
  const lastLog      = logs[0];

  // ── Données des cartes individuelles ─────────
  const SYNC_CARDS = [
    { key:"companies", label:"Entreprises", icon:"bi-building",       color:"#3B5BDB", dbCount: dbStats?.companies },
    { key:"assets",    label:"Assets",       icon:"bi-pc-display",     color:"#0077B6", dbCount: dbStats?.assets    },
    { key:"tickets",   label:"Tickets",      icon:"bi-ticket-detailed",color:"#6741D9", dbCount: dbStats?.tickets   },
  ];

  return (
    <>
      <Topbar title="Sync" subtitle="Atera"/>
      <div className="page-body">

        {/* ── En-tête ── */}
        <div className="page-header">
          <div>
            <div className="page-title">Synchronisation <span>Atera</span></div>
            <div className="page-sub">
              Importation des données Atera vers la base locale — companies, assets, tickets
            </div>
          </div>
          <div className="page-actions">
            {/* Bouton sync complète avec confirmation */}
            {!confirm ? (
              <button
                className="btn-itm btn-itm-primary"
                onClick={() => setConfirm(true)}
                disabled={isAnySyncing}
                style={{ minWidth:210 }}
              >
                <i className={`bi bi-arrow-repeat ${syncStatus.all?.syncing ? "spin" : ""}`}/>
                {syncStatus.all?.syncing ? "Synchronisation en cours…" : "Synchroniser tout"}
              </button>
            ) : (
              <div style={{ display:"flex", alignItems:"center", gap:8, background:"rgba(239,68,68,.08)", border:"1px solid rgba(239,68,68,.3)", borderRadius:10, padding:"8px 12px" }}>
                <span style={{ fontSize:13, color:"var(--text-2)" }}>Confirmer la sync complète ?</span>
                <button className="btn-itm btn-itm-primary" style={{ padding:"5px 14px", fontSize:12 }}
                  onClick={() => { setConfirm(false); runSync("all"); }}>
                  Oui
                </button>
                <button className="btn-itm btn-itm-outline" style={{ padding:"5px 14px", fontSize:12 }}
                  onClick={() => setConfirm(false)}>
                  Annuler
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Barre de progression sync complète ── */}
        {syncStatus.all?.syncing && (
          <div style={{ marginBottom:14 }}>
            <ProgressBar active color="var(--accent)"/>
            <div style={{ fontSize:12, color:"var(--text-3)", marginTop:6, textAlign:"center" }}>
              Synchronisation en cours — ne fermez pas cette page…
            </div>
          </div>
        )}

        {/* ── Métriques statut ── */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:20 }}>
          {[
            { icon:"bi-cloud-check",  color:"green",  label:"Connexion Atera",    value:"Active"                                        },
            { icon:"bi-clock",        color:"teal",   label:"Dernière sync",       value: lastLog ? timeAgo(lastLog.at) : "—"            },
            { icon:"bi-arrow-repeat", color:"purple", label:"Prochaine sync auto", value: countdown !== null ? timeIn(countdown) : "—"   },
            { icon:"bi-journal-text", color:"orange", label:"Syncs cette session", value: logs.length                                    },
          ].map((m, i) => (
            <div key={i} className={`metric-card mc-${m.color} anim-fade-up`} style={{ animationDelay:`${.05+i*.05}s` }}>
              <div className="metric-glow"/>
              <div className="metric-icon"><i className={`bi ${m.icon}`}/></div>
              <div className="metric-label">{m.label}</div>
              <div style={{ fontFamily:"Syne,sans-serif", fontSize:22, fontWeight:800, color:"var(--text-1)" }}>
                {m.value}
              </div>
            </div>
          ))}
        </div>

        {/* ── Cartes syncs individuelles ── */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:20 }}>
          {SYNC_CARDS.map(c => (
            <SyncCard
              key={c.key}
              typeKey={c.key}
              label={c.label}
              icon={c.icon}
              color={c.color}
              status={syncStatus[c.key]}
              onSync={() => runSync(c.key)}
              disabled={isAnySyncing}
              dbCount={c.dbCount}
            />
          ))}
        </div>

        {/* ── Résultats de la dernière sync complète ── */}
        {lastAll && !syncStatus.all?.syncing && (
          <div className="itm-card anim-fade-up" style={{ marginBottom:20 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
              <div className="card-title">Résultats — dernière sync complète</div>
              <span style={{ fontSize:11, color:"var(--text-3)" }}>
                {lastLog ? fmt(lastLog.at) : ""} · {lastLog?.duration}
              </span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14 }}>
              {[
                { label:"Entreprises", data: lastAll.companies, icon:"bi-building",       color:"#3B5BDB" },
                { label:"Assets",      data: lastAll.assets,    icon:"bi-pc-display",     color:"#0077B6" },
                { label:"Tickets",     data: lastAll.tickets,   icon:"bi-ticket-detailed",color:"#6741D9" },
              ].map(({ label, data, icon, color }) => (
                <div key={label} style={{ background:"var(--navy-soft)", borderRadius:10, padding:"14px 16px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                    <i className={`bi ${icon}`} style={{ color, fontSize:15 }}/>
                    <span style={{ fontSize:13, fontWeight:600, color:"var(--text-1)" }}>{label}</span>
                  </div>
                  {data ? (
                    <div style={{ display:"flex", flexDirection:"column", gap:4, fontSize:12 }}>
                      <span style={{ color:"var(--success)" }}>
                        <i className="bi bi-plus-circle me-1"/>+{data.created ?? 0} créés
                      </span>
                      <span style={{ color:"var(--accent)" }}>
                        <i className="bi bi-arrow-repeat me-1"/>{data.updated ?? 0} mis à jour
                      </span>
                      {(data.skipped ?? 0) > 0 && (
                        <span style={{ color:"var(--text-3)" }}>
                          <i className="bi bi-dash-circle me-1"/>{data.skipped} ignorés
                        </span>
                      )}
                    </div>
                  ) : (
                    <span style={{ fontSize:12, color:"var(--text-3)" }}>Aucune donnée</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Journal de session ── */}
        <div className="itm-card anim-fade-up delay-4">
          <div className="card-title-row">
            <div>
              <div className="card-title">Journal de synchronisation</div>
              <div className="card-sub">{logs.length} opération(s) cette session</div>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              {logs.length > 0 && (
                <>
                  <button className="btn-itm btn-itm-outline" style={{ fontSize:12, padding:"5px 12px" }}
                    onClick={() => exportLogs(logs)}>
                    <i className="bi bi-download me-1"/>Export CSV
                  </button>
                  <button className="btn-itm btn-itm-outline" style={{ fontSize:12, padding:"5px 12px", color:"var(--danger)", borderColor:"rgba(239,68,68,.3)" }}
                    onClick={() => { if (window.confirm("Effacer le journal ?")) setLogs([]); }}>
                    <i className="bi bi-trash me-1"/>Effacer
                  </button>
                </>
              )}
            </div>
          </div>

          {logs.length === 0 ? (
            <div style={{ textAlign:"center", padding:"30px 0", color:"var(--text-3)", fontSize:13 }}>
              <i className="bi bi-journal" style={{ fontSize:28, display:"block", marginBottom:10, opacity:.3 }}/>
              Aucune synchronisation effectuée dans cette session.
            </div>
          ) : (
            <table className="itm-table">
              <thead>
                <tr>
                  <th>Statut</th><th>Type</th><th>Créés</th><th>Mis à jour</th>
                  <th>Ignorés</th><th>Durée</th><th>Date/heure</th><th>Détail</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => {
                  const d       = l.data;
                  const created = d ? (d.companies?.created ?? 0) + (d.assets?.created ?? 0) + (d.tickets?.created ?? 0) : 0;
                  const updated = d ? (d.companies?.updated ?? 0) + (d.assets?.updated ?? 0) + (d.tickets?.updated ?? 0) : 0;
                  const skipped = d ? (d.companies?.skipped ?? 0) + (d.assets?.skipped ?? 0) + (d.tickets?.skipped ?? 0) : 0;

                  return (
                    <tr key={l.id}>
                      <td>
                        <span className={`badge-status ${l.status === "SUCCESS" ? "bs-resolved" : "bs-overdue"}`}>
                          <i className={`bi bi-${l.status === "SUCCESS" ? "check-circle" : "x-circle"} me-1`} style={{ fontSize:11 }}/>
                          {l.status}
                        </span>
                      </td>
                      <td style={{ fontSize:12, color:"var(--text-2)", textTransform:"capitalize" }}>
                        {l.type === "all" ? "Complète" : l.type}
                      </td>
                      <td style={{ color: created > 0 ? "var(--success)" : "var(--text-3)", fontSize:12 }}>
                        {l.status === "SUCCESS" ? `+${created}` : "—"}
                      </td>
                      <td style={{ color: updated > 0 ? "var(--accent)" : "var(--text-3)", fontSize:12 }}>
                        {l.status === "SUCCESS" ? updated : "—"}
                      </td>
                      <td style={{ color:"var(--text-3)", fontSize:12 }}>
                        {l.status === "SUCCESS" ? skipped : "—"}
                      </td>
                      <td style={{ color:"var(--text-3)", fontSize:12 }}>{l.duration}</td>
                      <td style={{ color:"var(--text-3)", fontSize:12 }}>{fmt(l.at)}</td>
                      <td style={{ fontSize:12, color: l.error ? "var(--danger)" : "var(--text-3)" }}>
                        {l.error || "OK"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Configuration ── */}
        <div className="itm-card anim-fade-up" style={{ marginTop:14 }}>
          <div className="card-title" style={{ marginBottom:16 }}>Configuration API Atera</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <div className="form-group">
              <label className="itm-label">URL de base</label>
              <input className="itm-input" defaultValue="https://app.atera.com/api/v3" readOnly style={{ opacity:.6 }}/>
            </div>
            <div className="form-group">
              <label className="itm-label">Clé API</label>
              <input className="itm-input" type="password" defaultValue="••••••••••••••••••••••••••" readOnly style={{ opacity:.6 }}/>
            </div>
          </div>
          <div style={{ marginTop:10, display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ flex:1, padding:"10px 14px", background:"rgba(16,185,129,.08)", border:"1px solid rgba(16,185,129,.2)", borderRadius:10, fontSize:13, color:"var(--success)", display:"flex", alignItems:"center", gap:8 }}>
              <i className="bi bi-check-circle-fill"/>
              <span>Connexion active — clé API définie dans <code style={{ fontSize:11, background:"rgba(255,255,255,.08)", padding:"2px 6px", borderRadius:4 }}>application.properties</code></span>
            </div>
            <button
              className="btn-itm btn-itm-outline"
              style={{ fontSize:12, padding:"8px 16px", whiteSpace:"nowrap" }}
              onClick={() => api.get("/atera/sync/debug/agent/1").then(() => alert("Connexion OK")).catch(() => alert("Connexion échouée"))}
            >
              <i className="bi bi-wifi me-1"/>Tester la connexion
            </button>
          </div>
        </div>

        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          .spin { animation: spin .7s linear infinite; display: inline-block; }
        `}</style>
      </div>
    </>
  );
}